import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { T } from '@tolgee/react';
import { getBlogPosts, getBlogPostBySlug, getStrapiMediaUrl } from '../../../lib/strapi';
import type { StrapiBlogPost } from '../../../lib/strapi/services';

// Parser para contenido estructurado de Strapi
const parseStructuredContent = (content: any): string => {
  if (!content) return '';
  
  // Si el contenido ya es string, devolverlo directamente
  if (typeof content === 'string') return content;
  
  // Si es un array, procesar cada elemento
  if (Array.isArray(content)) {
    return content.map(block => parseContentBlock(block)).join('');
  }
  
  // En cualquier otro caso, devolver string vacío
  return '';
};

// Convertir un bloque de contenido a HTML
const parseContentBlock = (block: any): string => {
  if (!block || !block.type) return '';
  
  switch (block.type) {
    case 'paragraph':
      return `<p class="mb-6 text-gray-800 leading-relaxed">${parseChildren(block.children)}</p>`;
    case 'heading':
      const level = block.level || 3;
      const headingClasses: Record<number, string> = {
        1: "text-4xl font-serif font-light mt-12 mb-6",
        2: "text-3xl font-serif font-light mt-10 mb-5",
        3: "text-2xl font-serif font-light mt-8 mb-4",
        4: "text-xl font-medium mt-6 mb-3",
        5: "text-lg font-medium mt-4 mb-2",
        6: "text-base font-medium mt-4 mb-2"
      };
      return `<h${level} class="${headingClasses[level] || ''}">${parseChildren(block.children)}</h${level}>`;
    case 'list':
      const listType = block.format === 'ordered' ? 'ol' : 'ul';
      const listClass = listType === 'ol' ? 'list-decimal' : 'list-disc';
      return `<${listType} class="${listClass} pl-6 mb-6 space-y-2">${parseChildren(block.children)}</${listType}>`;
    case 'list-item':
      return `<li class="ml-2">${parseChildren(block.children)}</li>`;
    case 'image':
      const url = block.image?.url || block.url || block.src || '';
      const alt = block.image?.alternativeText || block.alt || block.alternativeText || '';
      const caption = block.image?.caption || block.caption || '';
      return `<figure class="my-8"><img src="${url}" alt="${alt}" class="w-full h-auto rounded" />${caption ? `<figcaption class="text-center text-sm text-gray-600 mt-2 italic">${caption}</figcaption>` : ''}</figure>`;
    case 'quote':
    case 'blockquote':
      return `<blockquote class="pl-4 border-l-4 border-gray-300 italic my-8 text-gray-700 py-2">${parseChildren(block.children)}</blockquote>`;
    case 'code':
      const language = block.language || '';
      return `<pre class="bg-gray-100 p-4 overflow-x-auto rounded my-6 text-sm"><code class="language-${language}">${parseChildren(block.children)}</code></pre>`;
    case 'link':
      const href = block.url || '#';
      return `<a href="${href}" class="text-blue-600 hover:underline">${parseChildren(block.children)}</a>`;
    case 'table':
      return `<div class="overflow-x-auto my-8"><table class="w-full border-collapse">
        ${block.children ? parseChildren(block.children) : ''}
      </table></div>`;
    case 'table-row':
      return `<tr>${parseChildren(block.children)}</tr>`;
    case 'table-cell':
      const isHeader = block.header || false;
      const Tag = isHeader ? 'th' : 'td';
      const cellClass = isHeader ? 'font-medium bg-gray-100' : '';
      return `<${Tag} class="border border-gray-300 px-4 py-2 ${cellClass}">${parseChildren(block.children)}</${Tag}>`;
    case 'divider':
    case 'hr':
      return `<hr class="my-8 border-t border-gray-300" />`;
    case 'video':
      const videoUrl = block.url || '';
      return `<div class="video-container my-8">
        <iframe 
          src="${videoUrl}" 
          frameborder="0" 
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
          allowfullscreen
          class="w-full aspect-video rounded"
        ></iframe>
      </div>`;
    case 'text':
      let text = block.text || '';
      if (block.bold) text = `<strong class="font-bold">${text}</strong>`;
      if (block.italic) text = `<em class="italic">${text}</em>`;
      if (block.underline) text = `<u class="underline">${text}</u>`;
      if (block.strikethrough) text = `<s class="line-through">${text}</s>`;
      if (block.code) text = `<code class="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">${text}</code>`;
      return text;
    default:
      console.log('Tipo de bloque no soportado:', block.type, block);
      return '';
  }
};

// Procesar los hijos de un bloque
const parseChildren = (children: any[]): string => {
  if (!children || !Array.isArray(children)) return '';
  
  return children.map(child => {
    if (typeof child === 'string') return child;
    if (child.text) {
      let text = child.text;
      if (child.bold) text = `<strong class="font-bold">${text}</strong>`;
      if (child.italic) text = `<em class="italic">${text}</em>`;
      if (child.underline) text = `<u class="underline">${text}</u>`;
      if (child.strikethrough) text = `<s class="line-through">${text}</s>`;
      if (child.code) text = `<code class="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">${text}</code>`;
      if (child.link) text = `<a href="${child.link}" class="text-blue-600 hover:underline">${text}</a>`;
      return text;
    }
    return parseContentBlock(child);
  }).join('');
};

// Componente para artículos relacionados
const RelatedPosts = ({ 
  currentPostId, 
  categoryName 
}: { 
  currentPostId: number; 
  categoryName: string 
}) => {
  const [relatedPosts, setRelatedPosts] = useState<StrapiBlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRelatedPosts = async () => {
      try {
        // Obtener todos los posts y filtrar manualmente
        // Esto es un workaround ya que no tenemos un endpoint específico para posts relacionados
        const allPosts = await getBlogPosts(10);
        
        // Filtrar posts de la misma categoría que no sean el actual
        const filtered = allPosts
          .filter(post => post.id !== currentPostId)
          .filter(post => post.category?.name === categoryName || !categoryName)
          .slice(0, 2); // Limitar a 2 posts relacionados
        
        setRelatedPosts(filtered);
      } catch (error) {
        console.error('Error fetching related posts:', error);
        setRelatedPosts([]);
      } finally {
        setLoading(false);
      }
    };

    if (categoryName) {
      fetchRelatedPosts();
    }
  }, [currentPostId, categoryName]);

  // Si no hay posts relacionados o estamos cargando, no mostrar nada
  if (loading || relatedPosts.length === 0) return null;

  return (
    <section className="mt-24 border-t border-gray-100 pt-16">
      <h3 className="text-2xl font-serif font-light mb-10 text-center">
        <T keyName="blog.related_posts">Lecturas relacionadas</T>
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {relatedPosts.map(post => (
          <Link 
            key={post.id} 
            to={`/blog/${post.slug}`}
            className="group"
          >
            <article className="flex flex-col">
              <div className="mb-4 aspect-[4/3] overflow-hidden">
                <img 
                  src={getStrapiMediaUrl(post.image)} 
                  alt={post.title || ''} 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <span className="text-xs tracking-widest uppercase text-gray-500 mb-2">
                {post.category?.name || 'Blog'}
              </span>
              <h4 className="text-xl font-serif mb-2 font-light group-hover:text-gray-700 transition-colors">
                {post.title || post.Title || ''}
              </h4>
            </article>
          </Link>
        ))}
      </div>
    </section>
  );
};

const BlogPostDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<StrapiBlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [parsedContent, setParsedContent] = useState('');

  // Formatear fecha
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('es-ES', options);
  };

  // Buscar y cargar el post basado en el slug
  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        if (!slug) {
          setError('blog_post_not_found');
          setPost(null);
          return;
        }
        
        // Usamos la función de la librería para obtener el post por slug
        const postData = await getBlogPostBySlug(slug);
        
        if (postData) {
          setPost(postData);
          // Parsear el contenido si es necesario
          if (postData.content) {
            setParsedContent(parseStructuredContent(postData.content));
          }
          setError(null);
        } else {
          setPost(null);
          setError('blog_post_not_found');
        }
      } catch (err) {
        console.error("Error fetching blog post:", err);
        setError("blog_post_error_loading");
        setPost(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  // Manejo de estados de carga o post no encontrado
  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-black"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-3xl font-serif font-light text-gray-900 mb-4">
          {error === 'blog_post_not_found' ? <T keyName="blog.post_not_found">Artículo no encontrado</T> : <T keyName="blog.post_error_loading">Error al cargar el artículo</T>}
        </h1>
        <p className="text-gray-600 mb-8">
          {error === 'blog_post_not_found'
            ? <T keyName="blog.post_not_found_message">El artículo que estás buscando no existe o ha sido eliminado.</T>
            : <T keyName="blog.post_error_loading_message">Ha ocurrido un error al cargar el artículo. Por favor, intenta de nuevo más tarde.</T>}
        </p>
        <Link
          to="/blog"
          className="inline-block px-6 py-3 border border-black text-black hover:bg-black hover:text-white transition-colors duration-300"
        >
          <T keyName="blog.back_to_list">Volver al blog</T>
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.title || post.Title || 'Artículo'} | Blog Jarad</title>
        <meta name="description" content={post.excerpt || 'Artículo del blog de Jarad sobre nutrición y cuidado de mascotas'} />
      </Helmet>

    <div style={{backgroundColor: '#f2ede4'}} className="min-h-screen">
      {/* Hero Section */}
      <div className="py-24 bg-white">
        <div className="content-container">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link
              to="/blog"
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-800 transition-colors mb-8"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
              </svg>
              <T keyName="blog.back_to_list">Volver al blog</T>
            </Link>
            
            <span className="text-xs tracking-widest uppercase text-gray-500 mb-3 inline-block">
              {post.category?.name || 'Blog'}
            </span>
            
            <h1 className="text-4xl md:text-6xl font-extrabold text-center max-w-4xl mx-auto mb-6 tracking-tight" style={{color: '#0d0d0d'}}>
              {post.title || post.Title || ''}
            </h1>
            
            <p className="text-xl md:text-2xl font-light text-gray-600 max-w-3xl mx-auto text-center mb-8">
              {post.excerpt || ''}
            </p>
            
            <div className="flex items-center justify-center text-sm text-gray-500 mb-10 space-x-4">
              <span>{post.publishedAt ? formatDate(post.publishedAt) : ''}</span>
              <span>•</span>
              <span><T keyName="blog.read_time">5 min de lectura</T></span>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Imagen principal */}
      <div className="w-full aspect-[21/9] mb-16 overflow-hidden">
        <img 
          src={getStrapiMediaUrl(post.image)} 
          alt={post.title || ''} 
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* Contenido del artículo */}
      <div className="container mx-auto px-4">
        <article className="prose prose-lg md:prose-xl max-w-3xl mx-auto prose-headings:font-serif prose-headings:font-light prose-a:text-black prose-a:no-underline hover:prose-a:underline prose-blockquote:border-l-black prose-blockquote:not-italic prose-blockquote:font-serif">
          <div dangerouslySetInnerHTML={{ __html: parsedContent || post?.content || '' }} />
        </article>
        
        {/* Compartir artículo */}
        <div className="border-t border-gray-100 max-w-3xl mx-auto mt-16 pt-8">
          <p className="text-sm text-gray-600 mb-4 text-center"><T keyName="blog.share_post">¿Te ha gustado este artículo? Compártelo</T></p>
          <div className="flex justify-center space-x-6">
            <button className="text-gray-600 hover:text-black transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </button>
            <button className="text-gray-600 hover:text-black transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
              </svg>
            </button>
            <button className="text-gray-600 hover:text-black transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </button>
            <button className="text-gray-600 hover:text-black transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
              </svg>
            </button>
            <button className="text-gray-600 hover:text-black transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Artículos relacionados */}
        <RelatedPosts currentPostId={post.id} categoryName={post.category?.name || ''} />
        
        {/* Newsletter */}
        <div className="max-w-xl mx-auto my-24 text-center px-4">
          <h3 className="text-2xl font-serif mb-3 font-light">
            <T keyName="blog.newsletter_cta_title">¿Te interesa este tema?</T>
          </h3>
          <p className="text-gray-600 mb-6">
            <T keyName="blog.newsletter_cta_description">Suscríbete a nuestro newsletter para recibir más contenido como este.</T>
          </p>
          <form className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              placeholder="blog.newsletter_placeholder"
              className="flex-1 px-4 py-3 border border-gray-200 focus:outline-none focus:ring-1 focus:ring-black"
            />
            <button
              type="submit"
              className="bg-black text-white px-6 py-3 font-light hover:bg-gray-800 transition-colors"
            >
              <T keyName="blog.newsletter_subscribe">Suscribirse</T>
            </button>
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default BlogPostDetail; 